import React from 'react';
import PropTypes from 'prop-types';
import { PRICE_ADJUST } from 'constants/index';
import { Button, Input, Select } from 'sharedComponents';
import { InputAdornment, Typography } from '@material-ui/core';
import { getAdjustedPrice } from 'modules/clients/utils';

const PriceAdjust = ({
  classes,
  clientId,
  productName,
  productId,
  productPrice,
  adjustProductPrice,
  onClose,
  promoPrice,
  isConciergeCart,
}) => {
  const [adjustedPrice, setAdjustedPrice] = React.useState(null);
  const [priceExceeded, setPriceExceeded] = React.useState(false);
  const [priceInvalid, setPriceInvalid] = React.useState(false);
  const [priceType, setPriceType] = React.useState(PRICE_ADJUST.TYPES_CHARS.DOLLAR);
  const [inputValue, setInputValue] = React.useState('');
  const [selectValue, setSelectValue] = React.useState(PRICE_ADJUST.TYPES[0].value);
  const [reasonCode, setReasonCode] = React.useState();
  const [reasonDetails, setReasonDetails] = React.useState('');

  const isPercentsType = priceType === PRICE_ADJUST.TYPES_CHARS.PERCENT;

  const resetState = () => {
    setAdjustedPrice(null);
    setPriceExceeded(false);
    setPriceInvalid(false);
  };

  const handleSelectChange = (value) => {
    setSelectValue(value);
    setPriceType(PRICE_ADJUST.TYPES_CHARS[value]);

    // clear input value
    setInputValue('');
    resetState();
  };

  const handleInputChange = (value) => {
    const minPriceUSD = productPrice * (1 - PRICE_ADJUST.MAX_PRICE);

    setInputValue(value);
    resetState();

    // Validations
    if (!value.toString().length) {
      setAdjustedPrice(null);
      return;
    }

    const calcAdjustedPrice = getAdjustedPrice(productPrice, value, priceType);

    if (calcAdjustedPrice > productPrice || value < 0) {
      setPriceInvalid(true);
      return;
    }

    if (calcAdjustedPrice < minPriceUSD) {
      setPriceExceeded(true);
      return;
    }

    setAdjustedPrice(calcAdjustedPrice);
  };

  const handleReasonDetailsChange = (value = '') => {
    const MAX_LENGTH = 256;
    const text = value.length > MAX_LENGTH ? value.slice(0, MAX_LENGTH) : value;
    setReasonDetails(text);
  };

  const applyAdjustment = () => {
    const adjustedPriceData = {
      adjustedPrice: adjustedPrice.toFixed(2),
      priceOverrideReason: reasonCode,
      priceOverrideReasonDetails: reasonDetails.trim(),
      adjustmentValue: inputValue,
      priceType,
    };
    adjustProductPrice(clientId, productId, adjustedPriceData);
    onClose();
  };

  const isButtonDisabled = !(adjustedPrice && reasonCode && (!priceExceeded || !priceInvalid));
  return (
    <div className={classes.wrap}>
      <Typography>{productName}</Typography>
      <div className={classes.original}>
        <Typography variant="h5">{`${promoPrice ? 'Promo' : 'Original'} price`}</Typography>
        <Typography>$ {promoPrice || productPrice}</Typography>
      </div>
      <div className={classes.type}>
        <Select
          value={selectValue}
          onChange={handleSelectChange}
          label="Adjustment type"
          data-testid="select-adjustment-type"
        >
          {isConciergeCart ? PRICE_ADJUST.CONCIERGE_TYPES : PRICE_ADJUST.TYPES}
        </Select>
      </div>
      <div className={classes.type}>
        <Select value={reasonCode} onChange={setReasonCode} label="Reason" data-testid="select-reason-code">
          {isConciergeCart ? PRICE_ADJUST.CONCIERGE_REASONS : PRICE_ADJUST.REASONS}
        </Select>
      </div>
      <div className={classes.type}>
        <Input
          label="Enter the details for reason"
          value={reasonDetails}
          onChange={handleReasonDetailsChange}
          multiline
          data-testid="reason-details-input"
        />
      </div>
      <div className={classes.inputSet}>
        <div className={classes.inputContainer}>
          <Typography component="span">{isPercentsType ? '-' : priceType}</Typography>
          <Input
            type="number"
            label="Amount"
            fullWidth={false}
            value={inputValue}
            InputProps={{
              endAdornment: isPercentsType ? <InputAdornment position="end">%</InputAdornment> : null,
              inputProps: isPercentsType ? { inputMode: 'numeric' } : { inputMode: 'decimal' },
            }}
            InputLabelProps={{
              classes: { formControl: isPercentsType ? classes.inputLabelWithPercent : classes.inputLabel },
            }}
            onChange={handleInputChange}
            data-testid="adjustment-input"
          />
        </div>
        <div className={classes.errorMessage}>
          <Typography color="error" data-testid="adjust-price-error-msg">
            {priceExceeded && PRICE_ADJUST.ERROR_MSG}
            {priceInvalid && PRICE_ADJUST.INVALID_MSG}
          </Typography>
        </div>
      </div>
      <Button disabled={isButtonDisabled} onClick={applyAdjustment} data-testid="apply-adjustment-btn">
        Apply
      </Button>
    </div>
  );
};

PriceAdjust.propTypes = {
  clientId: PropTypes.string,
  productName: PropTypes.string,
  productId: PropTypes.string,
  productPrice: PropTypes.string,
  onClose: PropTypes.func,
  adjustProductPrice: PropTypes.func,
  classes: PropTypes.objectOf(PropTypes.string),
  isConciergeCart: PropTypes.bool,
};

PriceAdjust.defaultProps = {
  onClose: () => {},
  classes: {},
  isConciergeCart: false,
};

export default PriceAdjust;
